import { apiOpenDelivery } from "@/api"
import type {
  ClosedReason,
  PrepaidMethod,
  SchedulingChannel,
  SchedulingContext,
  SchedulingMessage,
  SchedulingSlot,
  TargetWindow,
  WindowCapacity,
} from "@/lib/scheduling/types"
import { bool, int, oneOf, record, text } from "@/lib/scheduling/wire"
import type { ServiceType } from "@/types"

const SERVICE_TYPES: ServiceType[] = ["DELIVERY", "TAKEOUT", "INDOOR"]

const MESSAGES: SchedulingMessage[] = [
  "NONE",
  "STORE_CLOSED_SCHEDULE_NEXT_WINDOW",
  "STORE_CLOSED_NO_SCHEDULING",
  "SCHEDULING_ONLY",
  "SCHEDULING_DISABLED",
  "WINDOW_FULL",
  "NO_UPCOMING_WINDOW",
]

const CLOSED_REASONS: ClosedReason[] = [
  "OUTSIDE_HOURS",
  "PAUSED",
  "HOLIDAY",
  "STORE_CLOSED",
  "SUSPENDED",
  "",
]

const PREPAID_METHODS: PrepaidMethod[] = ["PIX_ONLINE", "CARD_ONLINE"]

function parseTargetWindow(raw: unknown): TargetWindow | null {
  const source = record(raw)
  if (!source) return null
  const startsAt = text(source.startsAt)
  const endsAt = text(source.endsAt)
  if (!startsAt || !endsAt) return null
  return { startsAt, endsAt, isCurrent: bool(source.isCurrent) }
}

function parseSlots(raw: unknown): SchedulingSlot[] {
  if (!Array.isArray(raw)) return []
  const slots: SchedulingSlot[] = []
  for (const entry of raw) {
    const source = record(entry)
    if (!source) continue
    const readyAt = text(source.readyAt)
    if (!readyAt) continue
    slots.push({ readyAt, readyEndAt: text(source.readyEndAt) || readyAt })
  }
  return slots
}

function parseCapacity(raw: unknown): WindowCapacity {
  const source = record(raw)
  if (!source) return { max: 0, used: 0, remaining: null }
  const max = int(source.max)
  return {
    max,
    used: int(source.used),
    remaining: max > 0 && typeof source.remaining === "number" ? source.remaining : null,
  }
}

function parsePayment(raw: unknown) {
  const source = record(raw)
  const prepaid = Array.isArray(source?.prepaidMethods) ? source.prepaidMethods : []
  const pending = Array.isArray(source?.pendingMethodIds) ? source.pendingMethodIds : []
  return {
    prepaymentRequired: bool(source?.prepaymentRequired),
    prepaidMethods: prepaid.filter((m): m is PrepaidMethod =>
      PREPAID_METHODS.includes(m as PrepaidMethod),
    ),
    pendingMethodIds: pending.filter((id): id is string => typeof id === "string"),
  }
}

function parseChannel(raw: unknown): SchedulingChannel | null {
  const source = record(raw)
  if (!source) return null
  const serviceType = source.serviceType
  if (!SERVICE_TYPES.includes(serviceType as ServiceType)) return null

  return {
    serviceType: serviceType as ServiceType,
    openNow: bool(source.openNow),
    closedReason: oneOf(source.closedReason, CLOSED_REASONS, ""),
    instantAllowed: bool(source.instantAllowed),
    schedulingAllowed: bool(source.schedulingAllowed),
    schedulingOnly: bool(source.schedulingOnly),
    targetWindow: parseTargetWindow(source.targetWindow),
    slots: parseSlots(source.slots),
    capacity: parseCapacity(source.capacity),
    payment: parsePayment(source.payment),
    message: oneOf(source.message, MESSAGES, "NONE"),
  }
}

/** Narrows the untyped scheduling-context payload; unknown enum values fall back. */
export function parseSchedulingContext(raw: unknown): SchedulingContext | null {
  const source = record(raw)
  if (!source) return null
  const channels = Array.isArray(source.channels) ? source.channels : []
  const flags = record(source.featureFlags)

  return {
    timezone: text(source.timezone),
    featureFlags: { futureWindows: bool(flags?.futureWindows) },
    channels: channels
      .map(parseChannel)
      .filter((channel): channel is SchedulingChannel => channel !== null),
  }
}

/**
 * Reads the scheduling context for a merchant. Never cached (client or CDN):
 * it is precisely the payload that changes with the clock.
 */
export async function fetchSchedulingContext(
  merchantId: string,
  signal?: AbortSignal,
): Promise<SchedulingContext | null> {
  const { data } = await apiOpenDelivery.get<unknown>(`/${merchantId}/scheduling-context`, {
    signal,
    headers: { "Cache-Control": "no-cache" },
    params: { _: Date.now() },
  })
  return parseSchedulingContext(data)
}
